import { useEffect, useState, useMemo } from "react";
import { Box, Stack, CircularProgress } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales";
import ByzNurseSaveButton from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNurseSaveButton";
import ByzNursePrintButton2 from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNursePrintButton2";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import { useCardForm } from "src/hooks/useCardForm";
import { useAppSelector } from "src/app/hooks";

// Components
import { GeneralInfoSection } from "./components/GeneralInfoSection";
import { AssessmentField } from "./components/AssessmentField";
import { ScoreResultDisplay } from "./components/ScoreResultDisplay";

interface INewbornSkinFormData {
    NSRAS_ID: string;
    PROCESS_TIME: Date | null;
    CTIME: Date | null;
    CUSER: string;
    NURSE_ID: string;
    RISK_ASSESSMENT_ID: string;
    GENERAL_PHYSICAL_CONDITION: string;
    MENTAL_STATUS: string;
    MOVEMENT_ABILITY_ID: string;
    PHYSICAL_ACTIVITY_ID: string;
    NUTRITION_ID: string;
    SKIN_MOISTURE_CONDITION_ID: string;
    CALC_VALUE: number;
}

const MCP_IDS = {
    RISK_ASSESSMENT: 7,
    GENERAL_PHYSICAL_CONDITION: 100,
    MENTAL_STATUS: 101,
    MOVEMENT_ABILITY: 102,
    PHYSICAL_ACTIVITY: 103,
    NUTRITION: 104,
    SKIN_MOISTURE_CONDITION: 105,
    RESULT_NOTE: 106,
};

interface NewbornSkinRiskAssessmentProps {
    recordId?: string;
    patientId?: number;
    onClose?: () => void;
    save?: (id: string) => Promise<void>;
    handleChange?: (field: string, value: any) => void;
    isSaving?: boolean;
}

function NewbornSkinRiskAssessment({ recordId, patientId, onClose, save, isSaving, handleChange: parentHandleChange }: NewbornSkinRiskAssessmentProps) {
    const { t } = useTranslate();
    const user = useAppSelector((state) => (state.auth as any).user);

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertSeverity, setAlertSeverity] = useState<"error" | "success">("success");

    const form = useCardForm({
        cardType: "NEWBORN_SKIN_RISK_ASSESSMENT",
        recordId,
        user,
        onLoadRecord: (record: any) => {
            if (parentHandleChange) {
                // Eğer API'den gelen data 'columns' array'i içeriyorsa onu iterate et
                if (record.columns && Array.isArray(record.columns)) {
                    record.columns.forEach((col: { key: string; value: any }) => {
                        parentHandleChange(col.key, col.value);
                    });
                } else {
                    // Yoksa düz objeyi iterate et
                    Object.entries(record as Record<string, any>).forEach(([key, value]) => {
                        parentHandleChange(key, value);
                    });
                }
            }
        },
    });

    const {
        handleChange,
        handleMCPChange,
        getMCPParameterDetails,
        getScoreValue,
        mcpCacheLoaded,
        selectedMCPs,
    } = form;

    const formData = form.formData as INewbornSkinFormData;

    // Sync state with parent whenever formData changes
    useEffect(() => {
        if (parentHandleChange) {
            Object.entries(formData as Record<string, any>).forEach(([key, value]) => {
                parentHandleChange(key, value);
            });
        }
    }, [formData, parentHandleChange]);

    const showAlert = (message: string, severity: "error" | "success") => {
        setAlertMessage(message);
        setAlertSeverity(severity);
        setAlertOpen(true);
    };

    // Calculate score using getScoreValue from useCardForm
    const calculatedScore = useMemo((): number | null => {
        if (!mcpCacheLoaded) return null;

        const gpc = getScoreValue('GENERAL_PHYSICAL_CONDITION');
        const ms = getScoreValue('MENTAL_STATUS');
        const ma = getScoreValue('MOVEMENT_ABILITY_ID');
        const pa = getScoreValue('PHYSICAL_ACTIVITY_ID');
        const n = getScoreValue('NUTRITION_ID');
        const smc = getScoreValue('SKIN_MOISTURE_CONDITION_ID');

        // Newborn Skin Risk Assessment usually requires all fields for a valid score
        // Check if values are > 0 (assuming 0 means not selected or no score)
        if (gpc === 0 || ms === 0 || ma === 0 || pa === 0 || n === 0 || smc === 0) return null;

        return gpc + ms + ma + pa + n + smc;
    }, [mcpCacheLoaded, getScoreValue]);

    // Update calc_value in formData when calculatedScore changes
    useEffect(() => {
        if (calculatedScore !== null) {
            handleChange('CALC_VALUE', calculatedScore);
        }
    }, [calculatedScore, handleChange]);

    // Get result note based on score
    const resultNote = useMemo(() => {
        if (!mcpCacheLoaded || calculatedScore === null || calculatedScore === 0) return '';

        const resultDetails = getMCPParameterDetails(MCP_IDS.RESULT_NOTE);
        const matchingResult = resultDetails.find((d: any) => {
            const lowerValue = Number(d.lower_value) || 0;
            const topValue = Number(d.top_value) || 999;
            return calculatedScore >= lowerValue && calculatedScore <= topValue;
        });

        if (matchingResult) {
            return `${matchingResult.name}\n\n${matchingResult.note || ''}`;
        }
        return '';
    }, [calculatedScore, mcpCacheLoaded, getMCPParameterDetails]);

    const handleSaveMedcard = async () => {
        if (!save) return;
        try {
            // Ensure parent has the latest data before saving
            if (parentHandleChange) {
                Object.entries(formData as Record<string, any>).forEach(([key, value]) => {
                    parentHandleChange(key, value);
                });
            }
            await save(recordId || "");
            showAlert(t("operation-successful"), "success");
            if (onClose) setTimeout(onClose, 1500);
        } catch (error) {
            console.error("Save error:", error);
            showAlert(t("error-saving-record"), "error");
        }
    };

    if (form.loading && !mcpCacheLoaded) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress size={50} />
            </Box>
        );
    }

    return (
        <>
            <Stack
                sx={{ padding: "5px", marginBottom: "10px" }}
                direction="row"
                spacing={2}
            >
                <ByzNurseSaveButton onClick={handleSaveMedcard} loading={isSaving} />
                <ByzNursePrintButton2 />
            </Stack>

            <Box sx={{ padding: '10px', width: '100%' }}>
                <Grid container spacing={1}>
                    <Grid md={12} xs={12} container direction={'column'} spacing={1}>
                        {/* Title */}
                        <Grid md={12} xs={12} sx={{ backgroundColor: 'rgb(231, 239, 252)', borderBottom: '1px solid #E0E0E0', maxWidth: '50%', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                            <ByzMuiTypography isTitle sx={{ paddingLeft: '10px' }}>
                                {t("newborn-skin-risk-assessment.title")}
                            </ByzMuiTypography>
                        </Grid>

                        {/* General Info: DateTime and User */}
                        <GeneralInfoSection
                            processTime={formData.PROCESS_TIME}
                            onProcessTimeChange={(val) => handleChange('PROCESS_TIME', val)}
                            createdUser={formData.CUSER}
                        />

                        {/* Risk Assessment - Non-scoring field */}
                        <AssessmentField
                            label="newborn-skin-risk-assessment.risk-assessment"
                            mcpId={MCP_IDS.RISK_ASSESSMENT}
                            value={selectedMCPs.RISK_ASSESSMENT_ID}
                            onChange={(val) => handleMCPChange('RISK_ASSESSMENT_ID', val)}
                            getMcpParameterDetails={getMCPParameterDetails}
                            cacheLoaded={mcpCacheLoaded}
                            noBorder
                        />

                        {/* Assessments Section Header */}
                        <Grid md={12} xs={12} sx={{ backgroundColor: 'rgb(231, 239, 252)', borderBottom: '1px solid #E0E0E0', mt: 1, maxWidth: '50%', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                            <ByzMuiTypography isTitle sx={{ paddingLeft: '10px' }}>
                                {t("newborn-skin-risk-assessment.assessments-section")}
                            </ByzMuiTypography>
                        </Grid>

                        {/* Assessment Fields */}
                        <AssessmentField
                            label="newborn-skin-risk-assessment.general-physical-condition"
                            mcpId={MCP_IDS.GENERAL_PHYSICAL_CONDITION}
                            value={selectedMCPs.GENERAL_PHYSICAL_CONDITION}
                            onChange={(val) => handleMCPChange('GENERAL_PHYSICAL_CONDITION', val)}
                            getMcpParameterDetails={getMCPParameterDetails}
                            cacheLoaded={mcpCacheLoaded}
                        />

                        <AssessmentField
                            label="newborn-skin-risk-assessment.mental-status"
                            mcpId={MCP_IDS.MENTAL_STATUS}
                            value={selectedMCPs.MENTAL_STATUS}
                            onChange={(val) => handleMCPChange('MENTAL_STATUS', val)}
                            getMcpParameterDetails={getMCPParameterDetails}
                            cacheLoaded={mcpCacheLoaded}
                        />

                        <AssessmentField
                            label="newborn-skin-risk-assessment.mobility"
                            mcpId={MCP_IDS.MOVEMENT_ABILITY}
                            value={selectedMCPs.MOVEMENT_ABILITY_ID}
                            onChange={(val) => handleMCPChange('MOVEMENT_ABILITY_ID', val)}
                            getMcpParameterDetails={getMCPParameterDetails}
                            cacheLoaded={mcpCacheLoaded}
                        />

                        <AssessmentField
                            label="newborn-skin-risk-assessment.activity"
                            mcpId={MCP_IDS.PHYSICAL_ACTIVITY}
                            value={selectedMCPs.PHYSICAL_ACTIVITY_ID}
                            onChange={(val) => handleMCPChange('PHYSICAL_ACTIVITY_ID', val)}
                            getMcpParameterDetails={getMCPParameterDetails}
                            cacheLoaded={mcpCacheLoaded}
                        />

                        <AssessmentField
                            label="newborn-skin-risk-assessment.nutrition"
                            mcpId={MCP_IDS.NUTRITION}
                            value={selectedMCPs.NUTRITION_ID}
                            onChange={(val) => handleMCPChange('NUTRITION_ID', val)}
                            getMcpParameterDetails={getMCPParameterDetails}
                            cacheLoaded={mcpCacheLoaded}
                        />

                        <AssessmentField
                            label="newborn-skin-risk-assessment.moisture-status"
                            mcpId={MCP_IDS.SKIN_MOISTURE_CONDITION}
                            value={selectedMCPs.SKIN_MOISTURE_CONDITION_ID}
                            onChange={(val) => handleMCPChange('SKIN_MOISTURE_CONDITION_ID', val)}
                            getMcpParameterDetails={getMCPParameterDetails}
                            cacheLoaded={mcpCacheLoaded}
                            noBorder
                        />

                        {/* Other Section Header */}
                        <Grid md={12} xs={12} sx={{ backgroundColor: 'rgb(231, 239, 252)', borderBottom: '1px solid #E0E0E0', mt: 1, maxWidth: '50%', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                            <ByzMuiTypography isTitle sx={{ paddingLeft: '10px' }}>
                                {t("newborn-skin-risk-assessment.other-section")}
                            </ByzMuiTypography>
                        </Grid>

                        {/* Score and Result */}
                        <ScoreResultDisplay
                            score={calculatedScore}
                            resultNote={resultNote}
                        />

                        <ByzMuiSnackBar
                            open={alertOpen}
                            onClose={() => setAlertOpen(false)}
                            title={alertSeverity === "success" ? t("operation-successful") : t("error")}
                            message={alertMessage}
                            type={alertSeverity}
                        />
                    </Grid>
                </Grid>
            </Box>
        </>
    );
}

export default NewbornSkinRiskAssessment;
